// Shared interfaces for schedule data
export interface Subtopic {
  id: string;
  subtopicName?: string;
  mcq?: { level1?: number; level2?: number; level3?: number };
  coding?: { level1?: number; level2?: number; level3?: number };
  optionalMcq?: { level1?: number; level2?: number; level3?: number };
  optionalCoding?: { level1?: number; level2?: number; level3?: number };
  data: { type: string; path?: string; text: string; time: string; level: string }[];
}

export interface Topic {
  id: string;
  name: string;
  subtopics: Subtopic[];
}

export interface CourseData {
  [key: string]: {
    subject_name?: string;
    topics: Topic[];
  };
}

export type TestKind = 'Weekly Test' | 'Revision Test';

export interface TestDayTypeConfig {
  kind: 'test';
  testTypes: TestKind[];
}

/** Legacy string day types or Test multi-select config */
export type DayTypeEntry = string | TestDayTypeConfig;

export type DayTypeMap = Record<string, DayTypeEntry>;

export interface RevisionRef {
  subject: string;
  week: string;
}

export interface ScheduleDay {
  day: string;
  topic: string;
  date: Date | string;
  dayOfWeek: string;
  duration?: string;
  actualTime?: string;
  mcq?: { [key: string]: { level1?: number; level2?: number; level3?: number } };
  coding?: { [key: string]: { level1?: number; level2?: number; level3?: number } };
  optionalMcq?: { [key: string]: { level1?: number; level2?: number; level3?: number } };
  optionalCoding?: { [key: string]: { level1?: number; level2?: number; level3?: number } };
  subtopicids?: { subtopic_id: string; subtopic_name: string }[];
  content?: { [key: string]: { type: string; path?: string; text: string; time: string; level: string }[] };
  type?: 'Test';
  test_types?: TestKind[];
  /** One revision per day — single prior subject + week */
  revision?: RevisionRef;
  /** @deprecated legacy shape; stripped on save/regen */
  revision_subjects?: Record<string, string>;
  /** Live Session / workshop: subtopic ids for the session */
  subtopic_ids?: string[];
  /**
   * LCM attached to this learning day (same day topics complete — not a new day).
   * Batch can include/exclude these independently of the course plan.
   */
  live_session_lcm?: boolean;
  live_session_key?: string;
  live_session_title?: string;
}

export interface ScheduleData {
  [key: string]: ScheduleDay[];
}

export const LIVE_SESSION_TOPIC = 'Live Session';
export const ONSITE_WORKSHOP_TOPIC = 'Onsite Workshop';
export const ONLINE_WORKSHOP_TOPIC = 'Online Workshop';

export const DAY_TYPE_OPTIONS = [
  'Preparation Day',
  'Test',
  LIVE_SESSION_TOPIC,
  ONLINE_WORKSHOP_TOPIC,
  ONSITE_WORKSHOP_TOPIC,
  'Internals',
  'Semester Exam',
  'Festivals',
  'Other',
] as const;

export const LEGACY_SPECIAL_DAY_TOPICS = [
  'Preparation Day',
  'Weekly Test',
  'Revision Test',
  LIVE_SESSION_TOPIC,
  ONLINE_WORKSHOP_TOPIC,
  ONSITE_WORKSHOP_TOPIC,
  'Internals',
  'Semester Exam',
  'Festivals',
  'Other',
] as const;

const stripRevisionFields = (day: ScheduleDay): ScheduleDay => {
  const { revision: _r, revision_subjects: _rs, ...rest } = day;
  return rest;
};

export const getDayRevision = (day: ScheduleDay): RevisionRef | null => {
  if (day.revision?.subject && day.revision?.week) {
    return { subject: day.revision.subject, week: String(day.revision.week) };
  }
  if (day.revision_subjects) {
    const keys = Object.keys(day.revision_subjects);
    if (keys.length > 0) {
      const key = keys[0];
      const subject = day.revision_subjects[key];
      const match = /^subject(.+?)(\d+)$/.exec(key);
      if (match) {
        return { subject: subject || match[1], week: match[2] };
      }
    }
  }
  return null;
};

/** Stable display title for Weekly / Revision (and both). */
export const formatTestTypesTopic = (testTypes: TestKind[]): string => {
  const unique = Array.from(new Set(testTypes));
  const hasWeekly = unique.includes('Weekly Test');
  const hasRevision = unique.includes('Revision Test');
  if (hasWeekly && hasRevision) return 'Weekly Test and Revision Test';
  if (hasWeekly) return 'Weekly Test';
  if (hasRevision) return 'Revision Test';
  return unique.join(' and ');
};

/** Storage topic must stay Weekly-safe for legacy student/admin Weekly matching. */
export const storageTopicForTestTypes = (testTypes: TestKind[]): string => {
  if (testTypes.includes('Weekly Test')) return 'Weekly Test';
  if (testTypes.includes('Revision Test')) return 'Revision Test';
  return formatTestTypesTopic(testTypes);
};

export const parseTestTypesFromTopic = (topic: string | undefined | null): TestKind[] => {
  const text = topic || '';
  const types: TestKind[] = [];
  if (text.includes('Weekly Test')) types.push('Weekly Test');
  if (text.includes('Revision Test')) types.push('Revision Test');
  return types;
};

export const getDayTestTypes = (day: ScheduleDay): TestKind[] => {
  if (day.type === 'Test' && Array.isArray(day.test_types) && day.test_types.length > 0) {
    return [...day.test_types];
  }
  return parseTestTypesFromTopic(day.topic);
};

export const formatDayTopicLabel = (day: ScheduleDay): string => {
  const types = getDayTestTypes(day);
  if (types.length > 0) {
    const base = formatTestTypesTopic(types);
    const rev = getDayRevision(day);
    if (rev) {
      return `${base} (Rev: ${rev.subject} W${rev.week})`;
    }
    return base;
  }
  return day.topic || '';
};

export const buildTestDayPayload = (
  testTypes: TestKind[],
  revision?: RevisionRef
): ScheduleDay => {
  const ordered: TestKind[] = [];
  if (testTypes.includes('Weekly Test')) ordered.push('Weekly Test');
  if (testTypes.includes('Revision Test')) ordered.push('Revision Test');
  const payload: ScheduleDay = {
    day: '',
    topic: storageTopicForTestTypes(ordered),
    date: '',
    dayOfWeek: '',
    type: 'Test',
    test_types: ordered,
  };
  if (revision?.subject && revision?.week) {
    payload.revision = { subject: revision.subject, week: String(revision.week) };
  }
  return payload;
};

const subtopicIdsFromLearningDay = (day: ScheduleDay): string[] => {
  const ids: string[] = [];
  (day.subtopicids || []).forEach((sub) => {
    const subId = String(sub.subtopic_id || '').trim();
    if (subId && !ids.includes(subId)) ids.push(subId);
  });
  return ids;
};

const calendarWeekIndexes = (days: ScheduleDay[]): number[] => {
  let week = 1;
  let previousDay: string | null = null;
  return days.map((entry) => {
    if (entry.dayOfWeek === 'Mon' && previousDay !== null) week += 1;
    previousDay = entry.dayOfWeek;
    return week;
  });
};

const withoutLegacyLiveSessionTopicId = (day: ScheduleDay): ScheduleDay => {
  const { topic_id: _removed, ...rest } = day as ScheduleDay & { topic_id?: string[] };
  return rest;
};

/** Fill Live Session `subtopic_ids` with unique subtopic ids from learning days in the same week. */
export const applyLiveSessionSubtopicIds = (schedule: ScheduleData): ScheduleData => {
  const result: ScheduleData = {};

  Object.keys(schedule).forEach((subjectName) => {
    const days = [...(schedule[subjectName] || [])];
    const dated = days.filter((d) => hasValidScheduleDate(d.date));
    const undated = days.filter((d) => !hasValidScheduleDate(d.date));
    dated.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const weekIndexes = calendarWeekIndexes(dated);
    const subtopicIdsByWeek = new Map<number, string[]>();
    dated.forEach((day, index) => {
      if (isSpecialScheduleDay(day)) return;
      const week = weekIndexes[index];
      const next = subtopicIdsFromLearningDay(day);
      if (next.length === 0) return;
      const existing = subtopicIdsByWeek.get(week) || [];
      next.forEach((id) => {
        if (!existing.includes(id)) existing.push(id);
      });
      subtopicIdsByWeek.set(week, existing);
    });

    const stampedDated = dated.map((day, index) => {
      const cleaned = withoutLegacyLiveSessionTopicId(day);
      if (!isLiveSessionDay(cleaned)) return cleaned;
      // Keep plan-stamped ids when present; otherwise fill from same calendar week
      if (
        Array.isArray(cleaned.subtopic_ids) &&
        cleaned.subtopic_ids.length > 0
      ) {
        return cleaned;
      }
      return {
        ...cleaned,
        subtopic_ids: [...(subtopicIdsByWeek.get(weekIndexes[index]) || [])],
      };
    });

    result[subjectName] = [
      ...stampedDated,
      ...undated.map((day) => withoutLegacyLiveSessionTopicId(day)),
    ];
  });

  return result;
};

export const getSubjectWeekCount = (schedule: ScheduleData, subjectName: string): number => {
  const days = schedule[subjectName];
  if (!days?.length) return 0;
  const sorted = [...days].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  let week = 1;
  let previousDay: string | null = null;
  sorted.forEach((entry) => {
    if (entry.dayOfWeek === 'Mon' && previousDay !== null) week += 1;
    previousDay = entry.dayOfWeek;
  });
  return week;
};

const pickNextRevisionToken = (
  priorSubjects: string[],
  weekCounts: Record<string, number>,
  usedWeek: Record<string, number>,
  rrCursor: { value: number }
): RevisionRef | null => {
  if (priorSubjects.length === 0) return null;

  const allExhausted = priorSubjects.every(
    (s) => (usedWeek[s] ?? 0) >= (weekCounts[s] ?? 0)
  );
  if (allExhausted) {
    priorSubjects.forEach((s) => {
      usedWeek[s] = 0;
    });
    rrCursor.value = 0;
  }

  for (let attempt = 0; attempt < priorSubjects.length; attempt += 1) {
    const idx = (rrCursor.value + attempt) % priorSubjects.length;
    const subject = priorSubjects[idx];
    const used = usedWeek[subject] ?? 0;
    const max = weekCounts[subject] ?? 0;
    if (max > 0 && used < max) {
      const weekNum = used + 1;
      usedWeek[subject] = weekNum;
      rrCursor.value = (idx + 1) % priorSubjects.length;
      return { subject, week: String(weekNum) };
    }
  }
  return null;
};

/** Round-robin revision assignment; full pool reset when all prior weeks exhausted. */
export const applyRevisionAssignmentsToSchedule = (schedule: ScheduleData): ScheduleData => {
  const subjectNames = Object.keys(schedule);
  const weekCounts: Record<string, number> = {};
  subjectNames.forEach((s) => {
    weekCounts[s] = getSubjectWeekCount(schedule, s);
  });

  const usedWeek: Record<string, number> = {};
  const rrCursor = { value: 0 };
  const result: ScheduleData = {};

  subjectNames.forEach((subjectName, subjectIndex) => {
    const priorSubjects = subjectNames.slice(0, subjectIndex);
    result[subjectName] = (schedule[subjectName] || []).map((day) => {
      const testTypes = getDayTestTypes(day);
      if (!testTypes.includes('Revision Test') || priorSubjects.length === 0) {
        if (day.revision || day.revision_subjects) {
          return stripRevisionFields(day);
        }
        return { ...day };
      }

      const token = pickNextRevisionToken(priorSubjects, weekCounts, usedWeek, rrCursor);
      if (!token) return stripRevisionFields(day);

      const { revision_subjects: _rs, ...rest } = day;
      return {
        ...rest,
        type: 'Test' as const,
        test_types: testTypes,
        topic: storageTopicForTestTypes(testTypes),
        revision: token,
      };
    });
  });

  return result;
};

const specialDaySignatureFromEntry = (entry: DayTypeEntry): string => {
  if (typeof entry === 'string') return entry;
  return `Test:${[...entry.testTypes].sort().join(',')}`;
};

const specialDaySignature = (day: ScheduleDay): string => {
  if (day.type === 'Test' && day.test_types?.length) {
    return `Test:${[...day.test_types].sort().join(',')}`;
  }
  return day.topic || '';
};

const buildSpecialDayFromEntry = (
  entry: DayTypeEntry,
  dayLabel: string,
  date: string,
  dayOfWeek: string
): ScheduleDay => {
  if (typeof entry === 'string') {
    if (entry === LIVE_SESSION_TOPIC) {
      return { day: dayLabel, topic: LIVE_SESSION_TOPIC, date, dayOfWeek, subtopic_ids: [] };
    }
    if (
      entry === ONSITE_WORKSHOP_TOPIC ||
      entry === ONLINE_WORKSHOP_TOPIC
    ) {
      return { day: dayLabel, topic: entry, date, dayOfWeek, subtopic_ids: [] };
    }
    return { day: dayLabel, topic: entry, date, dayOfWeek };
  }
  const base = buildTestDayPayload(entry.testTypes);
  return { ...base, day: dayLabel, date, dayOfWeek };
};

export const isLiveSessionDay = (day: ScheduleDay): boolean =>
  (day.topic || '').trim() === LIVE_SESSION_TOPIC;

const dayTypeEntryMatchesDay = (day: ScheduleDay, entry: DayTypeEntry): boolean =>
  specialDaySignature(day) === specialDaySignatureFromEntry(entry);

/** True for Test / Preparation / Workshop / etc. — not learning content days. */
export const isSpecialScheduleDay = (day: ScheduleDay): boolean => {
  if (day.type === 'Test') return true;
  if (getDayTestTypes(day).length > 0) return true;
  const topic = day.topic || '';
  if ((LEGACY_SPECIAL_DAY_TOPICS as readonly string[]).includes(topic)) return true;
  if (topic.includes('Weekly Test') || topic.includes('Revision Test')) return true;
  return false;
};

/**
 * Keep learning days only (drop specials). Used when re-applying dayType so we
 * do not repack course JSON and wipe existing day boundaries.
 */
export const extractLearningOnlySchedule = (schedule: ScheduleData): ScheduleData => {
  const result: ScheduleData = {};
  Object.keys(schedule).forEach((subject) => {
    result[subject] = (schedule[subject] || []).filter((d) => !isSpecialScheduleDay(d));
  });
  return result;
};

const dayTypeDayNumber = (key: string): number => {
  const match = /^Day (\d+)$/.exec(key);
  return match ? parseInt(match[1], 10) : NaN;
};

export interface Selection {
  saturdayStudyDay: string;
  startDate: string;
  sundayHoliday: string;
  course_id: string;
  batch_id: string;
  hoursPerDay?: string;
}

const getSubtopicData = (subtopic: Subtopic) => {
  return Array.isArray(subtopic?.data) ? subtopic.data : [];
};

// Holiday check function with memoization
const holidayCache = new Map<string, boolean>();
export const isHoliday = (date: Date, saturdayStudyDay: string, sundayHoliday: string): boolean => {
  const dateKey = date.toISOString().split('T')[0];
  if (holidayCache.has(dateKey)) {
    return holidayCache.get(dateKey)!;
  }
  
  const dayOfWeek = date.getDay();
  const isHolidayResult = (dayOfWeek === 6 && saturdayStudyDay === 'Yes') ||
                         (dayOfWeek === 0 && sundayHoliday === 'Yes');
  
  holidayCache.set(dateKey, isHolidayResult);
  return isHolidayResult;
};

// Clear holiday cache when needed
export const clearHolidayCache = () => {
  holidayCache.clear();
};

// Memoized function to get the last date of a subject
const lastDateCache = new Map<string, string>();
export const getLastDateOfSubject = (schedule: ScheduleData, subjectName: string): string => {
  const cacheKey = `${subjectName}_${JSON.stringify(schedule)}`;
  if (lastDateCache.has(cacheKey)) {
    return lastDateCache.get(cacheKey)!;
  }
  
  const subjectDays = schedule[subjectName];
  if (!subjectDays?.length) return '';
  
  // Find max date without sorting
  let lastDate = subjectDays[0].date;
  for (let i = 1; i < subjectDays.length; i++) {
    const currentDate = subjectDays[i].date;
    if (new Date(currentDate).getTime() > new Date(lastDate).getTime()) {
      lastDate = currentDate;
    }
  }
  
  const result = typeof lastDate === 'string' ? lastDate : new Date(lastDate).toISOString().split('T')[0];
  lastDateCache.set(cacheKey, result);
  return result;
};

// Clear last date cache when needed
export const clearLastDateCache = () => {
  lastDateCache.clear();
};

// Get last date of previous subject
export const getLastDateOfPreviousSubject = (schedule: ScheduleData, currentSubjectName: string, startDate: string): string => {
  const subjects = Object.keys(schedule);
  const currentIndex = subjects.indexOf(currentSubjectName);
  
  if (currentIndex <= 0) return startDate;

  // Walk back to the nearest previous subject that already has dates
  for (let i = currentIndex - 1; i >= 0; i--) {
    const days = schedule[subjects[i]];
    if (!Array.isArray(days) || days.length === 0) continue;
    if (!days.some((d) => d.date)) continue;
    return getLastDateOfSubject(schedule, subjects[i]) || startDate;
  }

  return startDate;
};

// Get next start date for a subject
export const getNextStartDateForSubject = (schedule: ScheduleData, currentSubjectName: string, startDate: string, saturdayStudyDay: string, sundayHoliday: string): string => {
  const lastDateStr = getLastDateOfPreviousSubject(schedule, currentSubjectName, startDate);
  let nextDate = new Date(lastDateStr);
  nextDate.setDate(nextDate.getDate() + 1);
  while (isHoliday(nextDate, saturdayStudyDay, sundayHoliday)) {
    nextDate.setDate(nextDate.getDate() + 1);
  }
  return nextDate.toISOString().split('T')[0];
};

// Parse numeric day from label like "Day 10"
const dayNumberRegex = /Day\s+(\d+)/;
export const parseDayNumber = (label: string): number => {
  if (!label) return 0;
  const match = dayNumberRegex.exec(label);
  return match ? parseInt(match[1], 10) : 0;
};

// Get starting day number for a subject based on previous subjects
export const getStartingDayNumberForSubject = (schedule: ScheduleData, subjectName: string): number => {
  const subjects = Object.keys(schedule);
  const currentIndex = subjects.indexOf(subjectName);
  if (currentIndex <= 0) return 1;
  let maxDayNumber = 0;
  for (let i = 0; i < currentIndex; i++) {
    const subj = subjects[i];
    const days = schedule[subj] || [];
    days.forEach(d => {
      const n = parseDayNumber(d.day);
      if (!Number.isNaN(n) && n > maxDayNumber) maxDayNumber = n;
    });
  }
  return maxDayNumber + 1;
};

// Extract matching days for day types
export const extractMatchingDays = (data: ScheduleData, options: string[]): DayTypeMap => {
  const days: DayTypeMap = {};
  Object.values(data).forEach((subjectData) => {
    subjectData.forEach((dayData) => {
      if (dayData.type === 'Test' && Array.isArray(dayData.test_types) && dayData.test_types.length > 0) {
        days[dayData.day] = { kind: 'test', testTypes: [...dayData.test_types] };
      } else {
        const parsedTypes = parseTestTypesFromTopic(dayData.topic);
        if (parsedTypes.length > 0) {
          days[dayData.day] = { kind: 'test', testTypes: parsedTypes };
        } else if (options.includes(dayData.topic)) {
          days[dayData.day] = dayData.topic;
        }
      }
    });
  });
  return days;
};

// Convert course data to schedule data
export const convertToScheduleData = (selecteddata: CourseData, course_details: Selection): ScheduleData => {
  const schedule: ScheduleData = {};
  let currentDay = 0;
  let currentDate = new Date(course_details.startDate);

  const getHoursForDay = (day: number) => {
    const hoursPerDay = parseInt(course_details.hoursPerDay || '1', 10);
    return hoursPerDay;
  };

  Object.values(selecteddata).forEach((subjectData, subjectIndex) => {
    const subjectName = subjectData.subject_name || "Unknown Subject";
    schedule[subjectName] = [];
    let actualTime = 0;
    let daySchedule: any = null;
    let isFirstSubtopic = true;
    const topics = subjectData.topics || [];

    // For each new subject, ensure we start with proper date progression
    if (subjectIndex > 0) {
      // Move to next day after previous subject
      do {
        currentDate.setDate(currentDate.getDate() + 1);
      } while (isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday));
    }

    topics.forEach(topic => {
      const subtopics = topic.subtopics || [];
      subtopics.forEach(subtopic => {
        if (daySchedule === null || isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday)) {
          // Only shift date if it's a holiday, not just because daySchedule is null
          if (isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday)) {
            do {
              currentDate.setDate(currentDate.getDate() + 1);
            } while (isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday));
          }
          currentDay++;
          const dayHours = getHoursForDay(currentDay);
          daySchedule = {
            day: `Day ${currentDay}`,
            topic: "",
            date: currentDate.toISOString().split('T')[0],
            dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
            duration: dayHours.toString(),
            actualTime: "0.00",
            mcq: {},
            coding: {},
            optionalMcq: {},
            optionalCoding: {},
            subtopicids: [],
            content: {},
          };
          actualTime = 0;
        }

        const subtopicData = getSubtopicData(subtopic);
        const dataDuration = subtopicData.reduce((acc, item) => acc + parseFloat(item.time), 0) / 60;
        const mcqDuration = (subtopic.mcq?.level1 || 0) * 1 + (subtopic.mcq?.level2 || 0) * 1.5 + (subtopic.mcq?.level3 || 0) * 2;
        const codingDuration = (subtopic.coding?.level1 || 0) * 10 + (subtopic.coding?.level2 || 0) * 20 + (subtopic.coding?.level3 || 0) * 30;
        const totalDuration = dataDuration + (mcqDuration + codingDuration) / 60;
        const availableTime = parseFloat(daySchedule.duration);

        if (isFirstSubtopic || (actualTime + totalDuration <= availableTime)) {
          daySchedule.topic += (daySchedule.topic ? ", " : "") + `${subtopic.subtopicName}`;
          daySchedule.subtopicids.push({ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName });
          daySchedule.content[subtopic.id] = subtopicData;
          actualTime += totalDuration;

          if (subtopic.mcq) {
            daySchedule.mcq[subtopic.id] = {
              level1: subtopic.mcq?.level1 || 0,
              level2: subtopic.mcq?.level2 || 0,
              level3: subtopic.mcq?.level3 || 0,
            };
          }
          if (subtopic.coding) {
            daySchedule.coding[subtopic.id] = {
              level1: subtopic.coding?.level1 || 0,
              level2: subtopic.coding?.level2 || 0,
              level3: subtopic.coding?.level3 || 0,
            };
          }
          // Add optional content (but don't include in time calculation)
          if (subtopic.optionalMcq) {
            if (!daySchedule.optionalMcq) {
              daySchedule.optionalMcq = {};
            }
            daySchedule.optionalMcq[subtopic.id] = {
              level1: subtopic.optionalMcq?.level1 || 0,
              level2: subtopic.optionalMcq?.level2 || 0,
              level3: subtopic.optionalMcq?.level3 || 0,
            };
          }
          if (subtopic.optionalCoding) {
            if (!daySchedule.optionalCoding) {
              daySchedule.optionalCoding = {};
            }
            daySchedule.optionalCoding[subtopic.id] = {
              level1: subtopic.optionalCoding?.level1 || 0,
              level2: subtopic.optionalCoding?.level2 || 0,
              level3: subtopic.optionalCoding?.level3 || 0,
            };
          }

          isFirstSubtopic = false;
        } else {
          daySchedule.actualTime = actualTime.toFixed(2);
          schedule[subjectName].push(daySchedule);
          do {
            currentDate.setDate(currentDate.getDate() + 1);
          } while (isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday));
          currentDay++;
          const newDayHours = getHoursForDay(currentDay);
          actualTime = totalDuration;
          daySchedule = {
            day: `Day ${currentDay}`,
            topic: `${subtopic.subtopicName}`,
            date: currentDate.toISOString().split('T')[0],
            dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
            duration: newDayHours.toString(),
            actualTime: actualTime.toFixed(2),
            mcq: {},
            coding: {},
            subtopicids: [{ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName }],
            content: { [subtopic.id]: subtopicData },
          };

          if (subtopic.mcq) {
            daySchedule.mcq[subtopic.id] = {
              level1: subtopic.mcq?.level1 || 0,
              level2: subtopic.mcq?.level2 || 0,
              level3: subtopic.mcq?.level3 || 0,
            };
          }
          if (subtopic.coding) {
            daySchedule.coding[subtopic.id] = {
              level1: subtopic.coding?.level1 || 0,
              level2: subtopic.coding?.level2 || 0,
              level3: subtopic.coding?.level3 || 0,
            };
          }
          // Add optional content (but don't include in time calculation)
          if (subtopic.optionalMcq) {
            if (!daySchedule.optionalMcq) {
              daySchedule.optionalMcq = {};
            }
            daySchedule.optionalMcq[subtopic.id] = {
              level1: subtopic.optionalMcq?.level1 || 0,
              level2: subtopic.optionalMcq?.level2 || 0,
              level3: subtopic.optionalMcq?.level3 || 0,
            };
          }
          if (subtopic.optionalCoding) {
            if (!daySchedule.optionalCoding) {
              daySchedule.optionalCoding = {};
            }
            daySchedule.optionalCoding[subtopic.id] = {
              level1: subtopic.optionalCoding?.level1 || 0,
              level2: subtopic.optionalCoding?.level2 || 0,
              level3: subtopic.optionalCoding?.level3 || 0,
            };
          }
        }
      });
    });

    if (daySchedule) {
      daySchedule.actualTime = actualTime.toFixed(2);
      schedule[subjectName].push(daySchedule);
    }
  });

  return schedule;
};

// Edit schedule data for time changes
export const editScheduleData = (selecteddata: CourseData, course_details: Selection, hoursList: number[]): ScheduleData => {
  const schedule: ScheduleData = {};
  let currentDay = 0;
  let currentDate = new Date(course_details.startDate);

  const getHoursForDay = (day: number) => {
    const index = day - 1;
    if (index < hoursList.length) {
      return hoursList[index];
    }
    return hoursList.length > 0 ? hoursList[hoursList.length - 1] : 1;
  };

  Object.values(selecteddata).forEach(subjectData => {
    const subjectName = subjectData.subject_name || "Unknown Subject";
    schedule[subjectName] = [];
    let actualTime = 0;
    let daySchedule: any = null;
    let isFirstSubtopic = true;
    const topics = subjectData.topics || [];

    topics.forEach(topic => {
      const subtopics = topic.subtopics || [];
      subtopics.forEach(subtopic => {
        if (daySchedule === null || isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday)) {
          do {
            currentDate.setDate(currentDate.getDate() + 1);
          } while (isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday));
          currentDay++;
          const dayHours = getHoursForDay(currentDay);
          daySchedule = {
            day: `Day ${currentDay}`,
            topic: "",
            date: currentDate.toISOString().split('T')[0],
            dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
            duration: dayHours.toString(),
            actualTime: "0.00",
            mcq: {},
            coding: {},
            optionalMcq: {},
            optionalCoding: {},
            subtopicids: [],
            content: {},
          };
          actualTime = 0;
        }

        const subtopicData = getSubtopicData(subtopic);
        const dataDuration = subtopicData.reduce((acc, item) => acc + parseFloat(item.time), 0) / 60;
        const mcqDuration = (subtopic.mcq?.level1 || 0) * 1 + (subtopic.mcq?.level2 || 0) * 1.5 + (subtopic.mcq?.level3 || 0) * 2;
        const codingDuration = (subtopic.coding?.level1 || 0) * 10 + (subtopic.coding?.level2 || 0) * 20 + (subtopic.coding?.level3 || 0) * 30;
        const totalDuration = dataDuration + (mcqDuration + codingDuration) / 60;
        const availableTime = parseFloat(daySchedule.duration);

        if (isFirstSubtopic || (actualTime + totalDuration <= availableTime)) {
          daySchedule.topic += (daySchedule.topic ? ", " : "") + `${subtopic.subtopicName}`;
          daySchedule.subtopicids.push({ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName });
          daySchedule.content[subtopic.id] = subtopicData;
          actualTime += totalDuration;

          if (subtopic.mcq) {
            daySchedule.mcq[subtopic.id] = {
              level1: subtopic.mcq?.level1 || 0,
              level2: subtopic.mcq?.level2 || 0,
              level3: subtopic.mcq?.level3 || 0,
            };
          }
          if (subtopic.coding) {
            daySchedule.coding[subtopic.id] = {
              level1: subtopic.coding?.level1 || 0,
              level2: subtopic.coding?.level2 || 0,
              level3: subtopic.coding?.level3 || 0,
            };
          }
          // Add optional content (but don't include in time calculation)
          if (subtopic.optionalMcq) {
            if (!daySchedule.optionalMcq) {
              daySchedule.optionalMcq = {};
            }
            daySchedule.optionalMcq[subtopic.id] = {
              level1: subtopic.optionalMcq?.level1 || 0,
              level2: subtopic.optionalMcq?.level2 || 0,
              level3: subtopic.optionalMcq?.level3 || 0,
            };
          }
          if (subtopic.optionalCoding) {
            if (!daySchedule.optionalCoding) {
              daySchedule.optionalCoding = {};
            }
            daySchedule.optionalCoding[subtopic.id] = {
              level1: subtopic.optionalCoding?.level1 || 0,
              level2: subtopic.optionalCoding?.level2 || 0,
              level3: subtopic.optionalCoding?.level3 || 0,
            };
          }

          isFirstSubtopic = false;
        } else {
          daySchedule.actualTime = actualTime.toFixed(2);
          schedule[subjectName].push(daySchedule);
          do {
            currentDate.setDate(currentDate.getDate() + 1);
          } while (isHoliday(currentDate, course_details.saturdayStudyDay, course_details.sundayHoliday));
          currentDay++;
          const newDayHours = getHoursForDay(currentDay);
          actualTime = totalDuration;
          daySchedule = {
            day: `Day ${currentDay}`,
            topic: `${subtopic.subtopicName}`,
            date: currentDate.toISOString().split('T')[0],
            dayOfWeek: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
            duration: newDayHours.toString(),
            actualTime: actualTime.toFixed(2),
            mcq: {},
            coding: {},
            subtopicids: [{ subtopic_id: subtopic.id, subtopic_name: subtopic.subtopicName }],
            content: { [subtopic.id]: subtopicData },
          };

          if (subtopic.mcq) {
            daySchedule.mcq[subtopic.id] = {
              level1: subtopic.mcq?.level1 || 0,
              level2: subtopic.mcq?.level2 || 0,
              level3: subtopic.mcq?.level3 || 0,
            };
          }
          if (subtopic.coding) {
            daySchedule.coding[subtopic.id] = {
              level1: subtopic.coding?.level1 || 0,
              level2: subtopic.coding?.level2 || 0,
              level3: subtopic.coding?.level3 || 0,
            };
          }
          // Add optional content (but don't include in time calculation)
          if (subtopic.optionalMcq) {
            if (!daySchedule.optionalMcq) {
              daySchedule.optionalMcq = {};
            }
            daySchedule.optionalMcq[subtopic.id] = {
              level1: subtopic.optionalMcq?.level1 || 0,
              level2: subtopic.optionalMcq?.level2 || 0,
              level3: subtopic.optionalMcq?.level3 || 0,
            };
          }
          if (subtopic.optionalCoding) {
            if (!daySchedule.optionalCoding) {
              daySchedule.optionalCoding = {};
            }
            daySchedule.optionalCoding[subtopic.id] = {
              level1: subtopic.optionalCoding?.level1 || 0,
              level2: subtopic.optionalCoding?.level2 || 0,
              level3: subtopic.optionalCoding?.level3 || 0,
            };
          }
        }
      });
    });

    if (daySchedule) {
      daySchedule.actualTime = actualTime.toFixed(2);
      schedule[subjectName].push(daySchedule);
    }
  });

  return schedule;
};

// Get list of time for effect
export const getListOfTimeForEffect = (schedule: ScheduleData): number[] => {
  const durations: number[] = [];
  for (const subject of Object.values(schedule)) {
    for (const entry of subject) {
      if (isSpecialScheduleDay(entry)) continue;
      const duration = parseInt(String(entry.duration ?? '0'), 10) || 0;
      if (duration > 0) durations.push(duration);
    }
  }
  return durations;
};

// Get list of time for specific day
export const getListOfTime = (schedule: ScheduleData, day: string, inputHours: number): number[] => {
  const durations: number[] = [];
  for (const subject of Object.values(schedule)) {
    for (const entry of subject) {
      if (isSpecialScheduleDay(entry)) continue;
      if (entry.day === day) {
        durations.push(inputHours);
      } else {
        const duration = parseInt(String(entry.duration ?? '0'), 10) || 0;
        if (duration > 0) durations.push(duration);
      }
    }
  }
  return durations;
};

const hasValidScheduleDate = (value: ScheduleDay['date']): boolean => {
  if (!value) return false;
  const t = new Date(value).getTime();
  return Number.isFinite(t);
};

// Update schedule with custom date
export const updateScheduleWithCustomDate = (
  schedule: ScheduleData, 
  subjectName: string, 
  newStartDate: string, 
  saturdayStudyDay: string, 
  sundayHoliday: string
): ScheduleData => {
  const updatedSchedule = { ...schedule } as any;
  const subjects = Object.keys(updatedSchedule);
  const currentSubjectIndex = subjects.indexOf(subjectName);
  if (currentSubjectIndex === -1) return updatedSchedule as ScheduleData;

  let globalDayCounter = 1;
  for (let i = 0; i < currentSubjectIndex; i++) {
    const prevSubject = subjects[i];
    const prevSubjectDays = updatedSchedule[prevSubject];
    if (prevSubjectDays && prevSubjectDays.length > 0) {
      globalDayCounter += prevSubjectDays.length;
    }
  }

  const subjectDays = updatedSchedule[subjectName];
  if (!subjectDays || subjectDays.length === 0) return updatedSchedule as ScheduleData;

  let currentDate = new Date(newStartDate);

  subjectDays.forEach((day: any) => {
    while (isHoliday(currentDate, saturdayStudyDay, sundayHoliday)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const formatted = currentDate.toISOString().split('T')[0];
    day.date = formatted;
    day.dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
    day.day = `Day ${globalDayCounter}`;

    globalDayCounter++;
    currentDate.setDate(currentDate.getDate() + 1);
  });

  for (let i = currentSubjectIndex + 1; i < subjects.length; i++) {
    const nextSubject = subjects[i];
    const nextSubjectDays = updatedSchedule[nextSubject];
    if (nextSubjectDays && nextSubjectDays.length > 0) {
      nextSubjectDays.forEach((day: any) => {
        day.day = `Day ${globalDayCounter}`;
        globalDayCounter++;
      });
    }
  }
  return updatedSchedule as ScheduleData;
};

// Adjust subsequent subjects for collisions
export const adjustSubsequentSubjects = (
  schedule: ScheduleData, 
  changedSubjectName: string, 
  saturdayStudyDay: string, 
  sundayHoliday: string
): ScheduleData => {
  let updated = { ...schedule } as any;
  const subjects = Object.keys(updated);
  const changedSubjectIndex = subjects.indexOf(changedSubjectName);
  if (changedSubjectIndex === -1 || changedSubjectIndex >= subjects.length - 1) {
    return updated as ScheduleData;
  }

  const getLastDateOfSubjectIn = (sched: ScheduleData, subjectName: string): string => {
    const subjectDays = (sched as any)[subjectName];
    if (!subjectDays?.length) return '';

    const datedDays = subjectDays.filter((d: any) => !!d.date);
    if (datedDays.length === 0) return '';
    
    let lastDate = datedDays[0].date;
    for (let i = 1; i < datedDays.length; i++) {
      const currentDate = datedDays[i].date;
      if (new Date(currentDate).getTime() > new Date(lastDate).getTime()) {
        lastDate = currentDate;
      }
    }
    return typeof lastDate === 'string' ? lastDate : new Date(lastDate).toISOString().split('T')[0];
  };

  let changedSubjectLastDateStr = getLastDateOfSubjectIn(updated, changedSubjectName);
  if (!changedSubjectLastDateStr) return updated as ScheduleData;
  let changedSubjectEndDate = new Date(changedSubjectLastDateStr);

  for (let i = changedSubjectIndex + 1; i < subjects.length; i++) {
    const subsequentSubject = subjects[i];
    const subsequentSubjectDays = (updated as any)[subsequentSubject];
    if (!subsequentSubjectDays || subsequentSubjectDays.length === 0) continue;
    // Leave undated subjects alone — user will set start date
    if (!subsequentSubjectDays.some((d: any) => !!d.date)) continue;
    
    let earliestDate = subsequentSubjectDays[0].date;
    for (let j = 1; j < subsequentSubjectDays.length; j++) {
      const currentDate = subsequentSubjectDays[j].date;
      if (!currentDate) continue;
      if (!earliestDate || new Date(currentDate).getTime() < new Date(earliestDate).getTime()) {
        earliestDate = currentDate;
      }
    }
    if (!earliestDate) continue;
    const subsequentSubjectStartDate = new Date(earliestDate);
    
    if (subsequentSubjectStartDate <= changedSubjectEndDate) {
      const newSubsequentStartDate = new Date(changedSubjectEndDate);
      newSubsequentStartDate.setDate(newSubsequentStartDate.getDate() + 1);
      while (isHoliday(newSubsequentStartDate, saturdayStudyDay, sundayHoliday)) {
        newSubsequentStartDate.setDate(newSubsequentStartDate.getDate() + 1);
      }
      const newStartDateString = newSubsequentStartDate.toISOString().split('T')[0];
      
      updated = updateScheduleWithCustomDate(updated, subsequentSubject, newStartDateString, saturdayStudyDay, sundayHoliday);
      
      const updatedSubjectDays = (updated as any)[subsequentSubject];
      if (updatedSubjectDays && updatedSubjectDays.length > 0) {
        const updatedSortedDays = [...updatedSubjectDays].sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const updatedLastDate = updatedSortedDays[updatedSortedDays.length - 1].date;
        changedSubjectEndDate = new Date(updatedLastDate);
      }
    }
  }

  return updated as ScheduleData;
};

// Enforce sequential start dates
export const enforceSequentialStartDates = (
  schedule: ScheduleData, 
  startDate: string, 
  saturdayStudyDay: string, 
  sundayHoliday: string
): ScheduleData => {
  let updated = { ...schedule } as any;
  const subjectNames = Object.keys(updated);
  if (subjectNames.length === 0) return updated as ScheduleData;

  subjectNames.sort((a, b) => {
    const aFirst = new Date((updated[a]?.[0]?.date) || startDate).getTime();
    const bFirst = new Date((updated[b]?.[0]?.date) || startDate).getTime();
    return aFirst - bFirst;
  });

  let prevLastDate: Date | null = null;
  subjectNames.forEach((name) => {
    const days = updated[name];
    if (!days || days.length === 0) return;

    const sorted = [...days].sort((x: any, y: any) => new Date(x.date).getTime() - new Date(y.date).getTime());
    let first = new Date(sorted[0].date);
    const last = new Date(sorted[sorted.length - 1].date);

    if (prevLastDate && first <= prevLastDate) {
      const newStart = new Date(prevLastDate);
      newStart.setDate(newStart.getDate() + 1);
      while (isHoliday(newStart, saturdayStudyDay, sundayHoliday)) {
        newStart.setDate(newStart.getDate() + 1);
      }
      updated = updateScheduleWithCustomDate(updated, name, newStart.toISOString().split('T')[0], saturdayStudyDay, sundayHoliday);
      
      const refreshed = updated[name];
      if (refreshed && refreshed.length > 0) {
        const s2 = [...refreshed].sort((x: any, y: any) => new Date(x.date).getTime() - new Date(y.date).getTime());
        prevLastDate = new Date(s2[s2.length - 1].date);
      }
    } else {
      prevLastDate = last;
    }
  });

  return updated as ScheduleData;
};

// Edit schedule data with day type handling
export const editScheduleDataWithDayTypes = (
  updatedSchedule: ScheduleData,
  dayType: DayTypeMap,
  customSubjectDates: Record<string, string>,
  startDate: string,
  saturdayStudyDay: string,
  sundayHoliday: string
): ScheduleData => {
  const hasDayTypeChanges = Object.keys(dayType).length > 0;

  if (!hasDayTypeChanges) {
    const subjects = Object.keys(updatedSchedule);
    const result: ScheduleData = {};
    let globalDayCounter = 1;

    let globalDate: Date;
    try {
      globalDate = new Date(startDate);
      if (isNaN(globalDate.getTime())) {
        throw new Error('Invalid start date');
      }
    } catch (error) {
      console.error('Invalid start date, using current date:', startDate);
      globalDate = new Date();
    }

    while (isHoliday(globalDate, saturdayStudyDay, sundayHoliday)) {
      globalDate.setDate(globalDate.getDate() + 1);
    }

    for (const subject of subjects) {
      const days = updatedSchedule[subject] || [];
      const sortedDays = [...days];
      if (sortedDays.every((d) => hasValidScheduleDate(d.date))) {
        sortedDays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      }

      const customStart = customSubjectDates[subject];
      if (customStart) {
        try {
          const customStartDate = new Date(customStart);
          if (!isNaN(customStartDate.getTime()) && customStartDate > globalDate) {
            globalDate = new Date(customStart);
            while (isHoliday(globalDate, saturdayStudyDay, sundayHoliday)) {
              globalDate.setDate(globalDate.getDate() + 1);
            }
          }
        } catch (error) {
          console.error('Invalid custom start date for subject:', subject, customStart);
        }
      }

      result[subject] = sortedDays.map((day) => {
        if (isNaN(globalDate.getTime())) {
          globalDate = new Date();
        }

        while (isHoliday(globalDate, saturdayStudyDay, sundayHoliday)) {
          globalDate.setDate(globalDate.getDate() + 1);
        }

        const newDay: ScheduleDay = {
          ...day,
          day: `Day ${globalDayCounter}`,
          date: globalDate.toISOString().split('T')[0],
          dayOfWeek: globalDate.toLocaleDateString('en-US', { weekday: 'short' }),
        };

        globalDayCounter++;
        globalDate.setDate(globalDate.getDate() + 1);
        return newDay;
      });
    }

    return applyRevisionAssignmentsToSchedule(result);
  }

  const subjects = Object.keys(updatedSchedule);
  const getCurrentSubjectStart = (subject: string): string | null => {
    const days = updatedSchedule[subject];
    if (!days || days.length === 0) return null;
    const sorted = [...days].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const first = sorted[0].date;
    return typeof first === 'string' ? first : new Date(first).toISOString().split('T')[0];
  };

  const initialStarts: Record<string, string> = {};
  subjects.forEach((subject) => {
    if (customSubjectDates[subject]) {
      initialStarts[subject] = customSubjectDates[subject];
    } else {
      const fromCurrent = getCurrentSubjectStart(subject);
      initialStarts[subject] = fromCurrent || startDate;
    }
  });

  const result: ScheduleData = {};
  let globalDayCounter = 1;

  subjects.forEach((subject) => {
    const days = [...(updatedSchedule[subject] || [])];
    // Only sort by date when every day has a valid date; otherwise keep content order
    if (days.every((d) => hasValidScheduleDate(d.date))) {
      days.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
    result[subject] = [];

    let subjectDate = new Date(initialStarts[subject]);
    while (isHoliday(subjectDate, saturdayStudyDay, sundayHoliday)) {
      subjectDate.setDate(subjectDate.getDate() + 1);
    }

    days.forEach((day) => {
      let pendingEntry = dayType[`Day ${globalDayCounter}`];
      while (pendingEntry && !dayTypeEntryMatchesDay(day, pendingEntry)) {
        const dayLabel = `Day ${globalDayCounter}`;
        result[subject].push(
          buildSpecialDayFromEntry(
            pendingEntry,
            dayLabel,
            subjectDate.toISOString().split('T')[0],
            subjectDate.toLocaleDateString('en-US', { weekday: 'short' })
          )
        );

        globalDayCounter++;
        do {
          subjectDate.setDate(subjectDate.getDate() + 1);
        } while (isHoliday(subjectDate, saturdayStudyDay, sundayHoliday));
        pendingEntry = dayType[`Day ${globalDayCounter}`];
      }

      const dayLabel = `Day ${globalDayCounter}`;
      result[subject].push({
        ...day,
        day: dayLabel,
        date: subjectDate.toISOString().split('T')[0],
        dayOfWeek: subjectDate.toLocaleDateString('en-US', { weekday: 'short' }),
      });

      globalDayCounter++;
      do {
        subjectDate.setDate(subjectDate.getDate() + 1);
      } while (isHoliday(subjectDate, saturdayStudyDay, sundayHoliday));
    });

    while (dayType[`Day ${globalDayCounter}`]) {
      const dayLabel = `Day ${globalDayCounter}`;
      result[subject].push(
        buildSpecialDayFromEntry(
          dayType[dayLabel],
          dayLabel,
          subjectDate.toISOString().split('T')[0],
          subjectDate.toLocaleDateString('en-US', { weekday: 'short' })
        )
      );

      globalDayCounter++;
      do {
        subjectDate.setDate(subjectDate.getDate() + 1);
      } while (isHoliday(subjectDate, saturdayStudyDay, sundayHoliday));
    }
  });

  // Append any remaining dayType entries that sit past the last learning day
  // (e.g. "Insert to Next Day" after the final content day), including gaps.
  const remainingDayNums = Object.keys(dayType)
    .map(dayTypeDayNumber)
    .filter((n) => !Number.isNaN(n) && n >= globalDayCounter)
    .sort((a, b) => a - b);

  if (remainingDayNums.length > 0) {
    const lastSubject = subjects[subjects.length - 1];
    if (lastSubject) {
      if (!result[lastSubject]) result[lastSubject] = [];
      let subjectDate: Date;
      if (result[lastSubject].length > 0) {
        subjectDate = new Date(result[lastSubject][result[lastSubject].length - 1].date);
        do {
          subjectDate.setDate(subjectDate.getDate() + 1);
        } while (isHoliday(subjectDate, saturdayStudyDay, sundayHoliday));
      } else {
        subjectDate = new Date(initialStarts[lastSubject] || startDate);
        while (isHoliday(subjectDate, saturdayStudyDay, sundayHoliday)) {
          subjectDate.setDate(subjectDate.getDate() + 1);
        }
      }

      for (const _dayNum of remainingDayNums) {
        const entry = dayType[`Day ${_dayNum}`];
        if (!entry) continue;
        // Dense renumber from current counter so we never leave holes
        const dayLabel = `Day ${globalDayCounter}`;
        result[lastSubject].push(
          buildSpecialDayFromEntry(
            entry,
            dayLabel,
            subjectDate.toISOString().split('T')[0],
            subjectDate.toLocaleDateString('en-US', { weekday: 'short' })
          )
        );
        globalDayCounter++;
        do {
          subjectDate.setDate(subjectDate.getDate() + 1);
        } while (isHoliday(subjectDate, saturdayStudyDay, sundayHoliday));
      }
    }
  }

  const orderedSubjects = [...subjects].sort(
    (a, b) => new Date(initialStarts[a]).getTime() - new Date(initialStarts[b]).getTime()
  );

  for (let i = 1; i < orderedSubjects.length; i++) {
    const prevSubject = orderedSubjects[i - 1];
    const currSubject = orderedSubjects[i];
    const prevDays = result[prevSubject] || [];
    const currDays = result[currSubject] || [];

    if (prevDays.length > 0 && currDays.length > 0) {
      const prevLastDate = new Date(prevDays[prevDays.length - 1].date);
      const currFirstDate = new Date(currDays[0].date);

      if (currFirstDate <= prevLastDate) {
        let newStartDate = new Date(prevLastDate);
        do {
          newStartDate.setDate(newStartDate.getDate() + 1);
        } while (isHoliday(newStartDate, saturdayStudyDay, sundayHoliday));

        const daysToShift =
          (new Date(newStartDate).getTime() - currFirstDate.getTime()) / (1000 * 60 * 60 * 24);
        result[currSubject] = result[currSubject].map((day) => {
          const newDate = new Date(day.date);
          newDate.setDate(newDate.getDate() + daysToShift);
          while (isHoliday(newDate, saturdayStudyDay, sundayHoliday)) {
            newDate.setDate(newDate.getDate() + 1);
          }
          return {
            ...day,
            date: newDate.toISOString().split('T')[0],
            dayOfWeek: newDate.toLocaleDateString('en-US', { weekday: 'short' }),
          };
        });
      }
    }
  }

  return applyRevisionAssignmentsToSchedule(result);
};

// Utility function to update customSubjectDates when schedule gets sequenced
export const updateCustomSubjectDatesFromSchedule = (currentSchedule: ScheduleData): { [key: string]: string } => {
  const newCustomDates: { [key: string]: string } = {};
  
  Object.keys(currentSchedule).forEach(subjectName => {
    const subjectDays = currentSchedule[subjectName];
    if (Array.isArray(subjectDays) && subjectDays.length > 0) {
      const datedDays = subjectDays.filter((d: any) => d.date);
      if (datedDays.length === 0) return;
      const sortedDays = [...datedDays].sort((a: any, b: any) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const firstDate = sortedDays[0].date;
      const dateString = typeof firstDate === 'string' ? firstDate : new Date(firstDate).toISOString().split('T')[0];
      newCustomDates[subjectName] = dateString;
    }
  });
  
  return newCustomDates;
};

export interface CourseSubjectEntry {
  subjectId: string;
  subjectName: string;
}

// --- Subject reorder helpers (day-wise schedule create/edit) ---

const toDateOnlyString = (date: Date | string): string => {
  if (typeof date === 'string') {
    return date.split('T')[0];
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const extractSubjectNamesFromCourseData = (courseData: CourseData): CourseSubjectEntry[] => {
  return Object.entries(courseData).map(([subjectId, data]) => ({
    subjectId,
    subjectName: data.subject_name || subjectId,
  }));
};

export const extractSubjectNamesFromSchedule = (schedule: ScheduleData): string[] => {
  return Object.keys(schedule);
};

export const reorderCourseData = (courseData: CourseData, orderedNames: string[]): CourseData => {
  // Object key order drives editScheduleData / convertToScheduleData iteration order
  const nameToEntry = new Map<string, [string, CourseData[string]]>();
  Object.entries(courseData).forEach(([subjectId, data]) => {
    const name = data.subject_name || subjectId;
    nameToEntry.set(name, [subjectId, data]);
  });

  const reordered: CourseData = {};
  orderedNames.forEach((name) => {
    const entry = nameToEntry.get(name);
    if (entry) {
      reordered[entry[0]] = entry[1];
    }
  });

  Object.entries(courseData).forEach(([subjectId, data]) => {
    if (!reordered[subjectId]) {
      reordered[subjectId] = data;
    }
  });

  return reordered;
};

export const reorderScheduleBySubjectNames = (
  schedule: ScheduleData,
  orderedNames: string[]
): ScheduleData => {
  const reordered: ScheduleData = {};
  orderedNames.forEach((name) => {
    if (schedule[name]) {
      reordered[name] = schedule[name];
    }
  });
  Object.keys(schedule).forEach((name) => {
    if (!reordered[name]) {
      reordered[name] = schedule[name];
    }
  });
  return reordered;
};


/** Update one day's hours without rebuilding the schedule. */
export const updateDurationInPlace = (
  schedule: ScheduleData,
  selectedDay: string,
  hours: number | string
): ScheduleData => {
  const hoursValue = String(Number(hours) > 0 ? hours : 1);
  const next: ScheduleData = {};
  Object.keys(schedule).forEach((subject) => {
    next[subject] = (schedule[subject] || []).map((day) =>
      day.day === selectedDay ? { ...day, duration: hoursValue } : day
    );
  });
  return next;
};

/**
 * Recalculate learning content packing when hour capacity changes.
 *
 * Key requirement: do not wipe/remove later days — re-pack using existing
 * on-screen schedule content, then re-insert special days (Test/Weekly/RT)
 * at the same relative position to learning days.
 */
export const recalculateScheduleForHourChange = (
  schedule: ScheduleData,
  _courseData: CourseData,
  selection: Selection,
  selectedDay: string,
  hours: number,
  customSubjectDates: Record<string, string>
): ScheduleData => {
  const inputLearningCount = Object.values(schedule).reduce(
    (sum, days) => sum + (days || []).filter((d) => !isSpecialScheduleDay(d)).length,
    0
  );

  if (inputLearningCount === 0) {
    return updateDurationInPlace(schedule, selectedDay, hours);
  }

  // Capture special days relative to learning day count.
  const entries: { subjectName: string; day: ScheduleDay }[] = [];
  Object.keys(schedule).forEach((subjectName) => {
    (schedule[subjectName] || []).forEach((day) => entries.push({ subjectName, day }));
  });

  const parseSafe = (d: ScheduleDay) => parseDayNumber(d.day);
  entries.sort((a, b) => {
    const na = parseSafe(a.day);
    const nb = parseSafe(b.day);
    if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
    if (Number.isNaN(na)) return 1;
    if (Number.isNaN(nb)) return -1;
    return na - nb;
  });

  const scheduleDayToDayTypeEntry = (day: ScheduleDay): DayTypeEntry | null => {
    if (day.type === 'Test' && Array.isArray(day.test_types) && day.test_types.length > 0) {
      return { kind: 'test', testTypes: [...day.test_types] };
    }

    const topic = (day.topic || '').trim();
    if ((LEGACY_SPECIAL_DAY_TOPICS as readonly string[]).includes(topic)) {
      return topic;
    }

    const types = parseTestTypesFromTopic(topic);
    if (types.length > 0) {
      return { kind: 'test', testTypes: types };
    }

    return null;
  };

  const anchors: { afterLearningCount: number; entry: DayTypeEntry }[] = [];
  let learningCount = 0;
  for (const e of entries) {
    if (isSpecialScheduleDay(e.day)) {
      const entry = scheduleDayToDayTypeEntry(e.day);
      if (entry) anchors.push({ afterLearningCount: learningCount, entry });
    } else {
      learningCount += 1;
    }
  }

  // Capacity list per learning day in the *current* global order.
  const hoursList = getListOfTime(schedule, selectedDay, hours);

  // Extract content chunks from learning days and re-pack them per subject.
  type Chunk = {
    subtopicId: string;
    subtopicName: string;
    durationHours: number;
    contentItems: NonNullable<ScheduleDay['content']>[string];
    mcq?: { level1?: number; level2?: number; level3?: number };
    coding?: { level1?: number; level2?: number; level3?: number };
    optionalMcq?: { level1?: number; level2?: number; level3?: number };
    optionalCoding?: { level1?: number; level2?: number; level3?: number };
  };

  const levelCountsDurationHours = (
    mcq?: { level1?: number; level2?: number; level3?: number },
    coding?: { level1?: number; level2?: number; level3?: number }
  ): number => {
    const mcqMins =
      (mcq?.level1 || 0) * 1 + (mcq?.level2 || 0) * 1.5 + (mcq?.level3 || 0) * 2;
    const codingMins =
      (coding?.level1 || 0) * 10 + (coding?.level2 || 0) * 20 + (coding?.level3 || 0) * 30;
    return (mcqMins + codingMins) / 60;
  };

  const extractChunksFromDay = (day: ScheduleDay): Chunk[] => {
    const ids = day.subtopicids || [];
    if (ids.length > 0 && day.content) {
      return ids.map((st) => {
        const contentItems = day.content?.[st.subtopic_id] || [];
        const dataDurationHours =
          contentItems.reduce((acc, item) => acc + (parseFloat(item.time) || 0), 0) / 60;
        const practiceDurationHours = levelCountsDurationHours(
          day.mcq?.[st.subtopic_id],
          day.coding?.[st.subtopic_id]
        );
        return {
          subtopicId: st.subtopic_id,
          subtopicName: st.subtopic_name || st.subtopic_id,
          durationHours: Math.max(dataDurationHours + practiceDurationHours, 0.01),
          contentItems,
          mcq: day.mcq?.[st.subtopic_id],
          coding: day.coding?.[st.subtopic_id],
          optionalMcq: day.optionalMcq?.[st.subtopic_id],
          optionalCoding: day.optionalCoding?.[st.subtopic_id],
        };
      });
    }

    // Fallback: one chunk representing the whole day (topic-only row)
    const actual = parseFloat(String(day.actualTime ?? '0'));
    return [
      {
        subtopicId: `legacy-${day.day}`,
        subtopicName: day.topic || day.day,
        durationHours: Number.isFinite(actual) && actual > 0 ? actual : 0.01,
        contentItems: [],
      },
    ];
  };

  const getNextCapacity = (idx: number): number => {
    if (!hoursList.length) return 1;
    if (idx < hoursList.length) return hoursList[idx] || 1;
    return hoursList[hoursList.length - 1] || 1;
  };

  const subjectNames = Object.keys(schedule);
  let learningIdx = 0;
  const learningPacked: ScheduleData = {};

  for (const subjectName of subjectNames) {
    const learningDays = (schedule[subjectName] || [])
      .filter((d) => !isSpecialScheduleDay(d))
      .slice()
      .sort((a, b) => parseDayNumber(a.day) - parseDayNumber(b.day));

    const chunks = learningDays.flatMap(extractChunksFromDay);
    if (chunks.length === 0) {
      learningPacked[subjectName] = [];
      continue;
    }

    const packedDays: ScheduleDay[] = [];
    let dayCapacity = getNextCapacity(learningIdx);
    let actualTime = 0;
    let isFirstOnDay = true;

    let current: ScheduleDay = {
      day: '',
      topic: '',
      date: '',
      dayOfWeek: '',
      duration: String(dayCapacity),
      actualTime: '0.00',
      mcq: {},
      coding: {},
      optionalMcq: {},
      optionalCoding: {},
      subtopicids: [],
      content: {},
    };

    const pushCurrent = () => {
      if (!current.subtopicids?.length && !current.topic) return;
      current.actualTime = actualTime.toFixed(2);
      packedDays.push(current);
      learningIdx += 1;
      dayCapacity = getNextCapacity(learningIdx);
      actualTime = 0;
      isFirstOnDay = true;
      current = {
        day: '',
        topic: '',
        date: '',
        dayOfWeek: '',
        duration: String(dayCapacity),
        actualTime: '0.00',
        mcq: {},
        coding: {},
        optionalMcq: {},
        optionalCoding: {},
        subtopicids: [],
        content: {},
      };
    };

    for (const chunk of chunks) {
      if (!isFirstOnDay && actualTime + chunk.durationHours > dayCapacity) {
        pushCurrent();
      }

      current.topic = current.topic ? `${current.topic}, ${chunk.subtopicName}` : chunk.subtopicName;
      current.subtopicids = [
        ...(current.subtopicids || []),
        { subtopic_id: chunk.subtopicId, subtopic_name: chunk.subtopicName },
      ];
      current.content = current.content || {};
      current.content[chunk.subtopicId] = chunk.contentItems;

      if (chunk.mcq) {
        current.mcq = current.mcq || {};
        current.mcq[chunk.subtopicId] = chunk.mcq;
      }
      if (chunk.coding) {
        current.coding = current.coding || {};
        current.coding[chunk.subtopicId] = chunk.coding;
      }
      if (chunk.optionalMcq) {
        current.optionalMcq = current.optionalMcq || {};
        current.optionalMcq[chunk.subtopicId] = chunk.optionalMcq;
      }
      if (chunk.optionalCoding) {
        current.optionalCoding = current.optionalCoding || {};
        current.optionalCoding[chunk.subtopicId] = chunk.optionalCoding;
      }

      actualTime += chunk.durationHours;
      isFirstOnDay = false;
    }

    pushCurrent();
    learningPacked[subjectName] = packedDays;
  }

  const outputLearningCount = Object.values(learningPacked).reduce(
    (sum, days) => sum + (days || []).length,
    0
  );

  if (outputLearningCount === 0) {
    return updateDurationInPlace(schedule, selectedDay, hours);
  }

  // Build dayType map from anchors relative to new packed learning count.
  const totalLearning = outputLearningCount;
  const normalizedAnchors = anchors
    .map((a) => ({
      afterLearningCount: Math.max(0, Math.min(a.afterLearningCount, totalLearning)),
      entry: a.entry,
    }))
    .sort((a, b) => a.afterLearningCount - b.afterLearningCount);

  const dayTypeMap: DayTypeMap = {};
  let finalDay = 1;
  let learningSeen = 0;
  let anchorIdx = 0;

  while (anchorIdx < normalizedAnchors.length && normalizedAnchors[anchorIdx].afterLearningCount === 0) {
    dayTypeMap[`Day ${finalDay}`] = normalizedAnchors[anchorIdx].entry;
    finalDay += 1;
    anchorIdx += 1;
  }

  for (let i = 0; i < totalLearning; i++) {
    finalDay += 1; // learning day
    learningSeen += 1;
    while (
      anchorIdx < normalizedAnchors.length &&
      normalizedAnchors[anchorIdx].afterLearningCount === learningSeen
    ) {
      dayTypeMap[`Day ${finalDay}`] = normalizedAnchors[anchorIdx].entry;
      finalDay += 1;
      anchorIdx += 1;
    }
  }

  while (anchorIdx < normalizedAnchors.length) {
    dayTypeMap[`Day ${finalDay}`] = normalizedAnchors[anchorIdx].entry;
    finalDay += 1;
    anchorIdx += 1;
  }

  return editScheduleDataWithDayTypes(
    learningPacked,
    dayTypeMap,
    customSubjectDates,
    selection.startDate,
    selection.saturdayStudyDay,
    selection.sundayHoliday
  );
};

const findMaxDayContainingSubjectIndex = (
  schedule: ScheduleData,
  maxDay: number
): number => {
  // Subject that contains the student's current max alterable day (batch progress)
  if (maxDay <= 0) return -1;

  const subjectNames = Object.keys(schedule);
  for (let i = 0; i < subjectNames.length; i++) {
    const days = schedule[subjectNames[i]];
    if (!Array.isArray(days) || days.length === 0) continue;
    const dayNums = days.map((d) => parseDayNumber(d.day));
    const min = Math.min(...dayNums);
    const max = Math.max(...dayNums);
    if (maxDay >= min && maxDay <= max) {
      return i;
    }
  }

  for (let i = subjectNames.length - 1; i >= 0; i--) {
    const days = schedule[subjectNames[i]];
    if (Array.isArray(days) && days.some((d) => parseDayNumber(d.day) <= maxDay)) {
      return i;
    }
  }

  return -1;
};

export const isSubjectReorderLocked = (
  schedule: ScheduleData,
  subjectName: string,
  today: Date = new Date(),
  maxDay: number = 0
): boolean => {
  // Locked if any dated day is today/past, or subject is at/before max-day progress in order
  const subjectNames = Object.keys(schedule);
  const subjectIndex = subjectNames.indexOf(subjectName);
  if (subjectIndex === -1) return false;

  const subjectDays = schedule[subjectName];
  if (Array.isArray(subjectDays) && subjectDays.length > 0) {
    const todayStr = toDateOnlyString(today);
    // Empty/missing dates are "not scheduled" — do not treat as past
    if (subjectDays.some((day) => day.date && toDateOnlyString(day.date) <= todayStr)) {
      return true;
    }
  }

  if (maxDay > 0) {
    const containingIndex = findMaxDayContainingSubjectIndex(schedule, maxDay);
    // Lock this subject and all earlier subjects in schedule order
    if (containingIndex >= 0 && subjectIndex <= containingIndex) {
      // Newly mapped undated subjects may still carry Day 1..N labels — don't lock them
      if (subjectIndex < containingIndex || subjectHasDates(schedule, subjectName)) {
        return true;
      }
    }
  }

  return false;
};

/** Renumber day labels to Day 1..N in schedule order. Days <= maxDay keep their numbers. */
export const renumberScheduleDays = (
  schedule: ScheduleData,
  maxDay: number = 0
): ScheduleData => {
  const result: ScheduleData = {};
  let counter = 1;

  Object.keys(schedule).forEach((subjectName) => {
    const days = schedule[subjectName];
    if (!Array.isArray(days) || days.length === 0) {
      result[subjectName] = days || [];
      return;
    }

    result[subjectName] = days.map((day) => {
      const dayNum = parseDayNumber(day.day);
      // Only freeze numbered days that already have dates (student progress).
      // Undated newly mapped subjects may still carry Day 1..N and must renumber.
      if (maxDay > 0 && dayNum > 0 && dayNum <= maxDay && day.date) {
        counter = Math.max(counter, dayNum + 1);
        return day;
      }
      const next = { ...day, day: `Day ${counter}` };
      counter += 1;
      return next;
    });
  });

  return result;
};

/**
 * Align later subjects to start on the next working day after the previous subject.
 * Fixes both gaps (unselect middle subject) and overlaps (newly checked subject generated from batch start).
 */
export const closeScheduleGapsAfterSubject = (
  schedule: ScheduleData,
  fromSubjectName: string,
  saturdayStudyDay: string,
  sundayHoliday: string,
  maxDay: number = 0
): ScheduleData => {
  let updated: ScheduleData = { ...schedule };
  const subjects = Object.keys(updated);
  const startIndex = Math.max(0, subjects.indexOf(fromSubjectName));

  for (let i = startIndex; i < subjects.length - 1; i++) {
    const prevDays = updated[subjects[i]];
    const nextDays = updated[subjects[i + 1]];
    if (!Array.isArray(prevDays) || prevDays.length === 0) continue;
    if (!Array.isArray(nextDays) || nextDays.length === 0) continue;

    // Skip subjects with no dates yet (newly mapped — user will set start date)
    const prevHasDates = prevDays.some((d) => d.date);
    const nextHasDates = nextDays.some((d) => d.date);
    if (!prevHasDates || !nextHasDates) continue;

    // Do not shift a next subject if any of its days are within maxDay restriction
    if (
      maxDay > 0 &&
      nextDays.some((d) => {
        const n = parseDayNumber(d.day);
        return n > 0 && n <= maxDay;
      })
    ) {
      continue;
    }

    const sortedPrev = [...prevDays].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const sortedNext = [...nextDays].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const prevLast = sortedPrev[sortedPrev.length - 1];
    const nextFirst = sortedNext[0];

    let expectedStart = new Date(prevLast.date);
    do {
      expectedStart.setDate(expectedStart.getDate() + 1);
    } while (isHoliday(expectedStart, saturdayStudyDay, sundayHoliday));

    const expectedStr = expectedStart.toISOString().split('T')[0];
    const nextStr = toDateOnlyString(nextFirst.date);
    // Gap (next > expected) or overlap (next before expected) → align to day after previous subject
    if (nextStr !== expectedStr) {
      updated = updateScheduleWithCustomDate(
        updated,
        subjects[i + 1],
        expectedStr,
        saturdayStudyDay,
        sundayHoliday
      );
    }
  }

  return renumberScheduleDays(updated, maxDay);
};

export const filterCourseDataByIds = (
  courseData: CourseData,
  orderedIds: string[]
): CourseData => {
  const filtered: CourseData = {};
  orderedIds.forEach((id) => {
    if (courseData[id]) {
      filtered[id] = courseData[id];
    }
  });
  return filtered;
};

export const subjectIdsToNames = (courseData: CourseData, ids: string[]): string[] => {
  return ids.map((id) => courseData[id]?.subject_name || id);
};

export const subjectNamesToIds = (courseData: CourseData, names: string[]): string[] => {
  const nameToId = new Map<string, string>();
  Object.entries(courseData).forEach(([id, data]) => {
    nameToId.set(data.subject_name || id, id);
  });
  return names.map((name) => nameToId.get(name) || name);
};

export const filterScheduleBySelectedNames = (
  schedule: ScheduleData,
  selectedNames: string[]
): ScheduleData => {
  const filtered: ScheduleData = {};
  selectedNames.forEach((name) => {
    if (schedule[name] !== undefined) {
      filtered[name] = schedule[name];
    }
  });
  return filtered;
};

/**
 * Keep the current calendar/subject order when checking/unchecking subjects.
 * Newly selected names are appended; remaining names stay in their existing order.
 * Never rebuild from allSubjects / course-data / subject_released order (those are often alphabetical).
 */
export const mergeSubjectSelectionOrder = (
  currentOrder: string[],
  newSelected: string[]
): string[] => {
  const selectedSet = new Set(newSelected);
  const kept = currentOrder.filter((name) => selectedSet.has(name));
  const added = newSelected.filter((name) => !currentOrder.includes(name));
  return [...kept, ...added];
};

/**
 * Date realignment (gap close / push later subjects) is only needed when dated
 * subjects are reordered or a dated subject is removed. Checking or unchecking
 * an undated subject must not rewrite existing calendar dates.
 */
export const datedSubjectOrderChanged = (
  previousOrder: string[],
  newOrder: string[],
  schedule: ScheduleData
): boolean => {
  const previousDated = previousOrder.filter((name) => subjectHasDates(schedule, name));
  const nextDated = newOrder.filter((name) => subjectHasDates(schedule, name));
  if (previousDated.length !== nextDated.length) return true;
  return previousDated.some((name, i) => name !== nextDated[i]);
};

export const getSubjectStartDate = (
  schedule: ScheduleData,
  subjectName: string
): string | null => {
  const subjectDays = schedule[subjectName];
  if (!Array.isArray(subjectDays) || subjectDays.length === 0) return null;
  const datedDays = subjectDays.filter((d) => !!d.date);
  if (datedDays.length === 0) return null;
  const sorted = [...datedDays].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  return toDateOnlyString(sorted[0].date);
};

/** True when the subject has at least one calendar date assigned. */
export const subjectHasDates = (schedule: ScheduleData, subjectName: string): boolean => {
  const days = schedule[subjectName];
  return Array.isArray(days) && days.some((d) => !!d.date);
};

/**
 * After hour/day-type regenerate, restore empty dates for subjects that were still undated.
 * Prevents auto-filling "Set date" subjects.
 */
export const preserveUndatedSubjectDates = (
  previous: ScheduleData,
  next: ScheduleData
): ScheduleData => {
  const result: ScheduleData = { ...next };
  Object.keys(result).forEach((name) => {
    if (subjectHasDates(previous, name)) return;
    const days = result[name];
    if (!Array.isArray(days) || days.length === 0) return;
    result[name] = days.map((day) => ({
      ...day,
      date: '',
      dayOfWeek: '',
    }));
  });
  return result;
};

export const isSubjectAccessedByMaxDay = (
  schedule: ScheduleData,
  subjectName: string,
  maxDay: number = 0
): boolean => {
  if (maxDay <= 0) return false;
  // Undated / newly mapped subjects are not student-progress locked
  if (!subjectHasDates(schedule, subjectName)) return false;
  const subjectNames = Object.keys(schedule);
  const subjectIndex = subjectNames.indexOf(subjectName);
  if (subjectIndex === -1) return false;
  const containingIndex = findMaxDayContainingSubjectIndex(schedule, maxDay);
  return containingIndex >= 0 && subjectIndex <= containingIndex;
};

/** Unselect allowed when start date >= today and subject is not reached via max_day progress. */
export const canUnselectSubject = (
  schedule: ScheduleData,
  subjectName: string,
  today: Date = new Date(),
  maxDay: number = 0
): boolean => {
  if (isSubjectAccessedByMaxDay(schedule, subjectName, maxDay)) {
    return false;
  }

  const startDate = getSubjectStartDate(schedule, subjectName);
  if (startDate === null) {
    // Not yet scheduled — safe to unselect if max_day did not lock it
    return true;
  }

  return startDate >= toDateOnlyString(today);
};

/** Normalize API subject_released (IDs) or default to IDs of subjects already in the batch schedule. */
export const resolveSubjectReleasedIds = (
  subjectReleased: string[] | undefined | null,
  courseData: CourseData,
  scheduleSubjectNames: string[]
): string[] => {
  const resolveOne = (token: string): string | null => {
    if (courseData[token]) return token;
    const byName = Object.entries(courseData).find(
      ([, data]) => (data.subject_name || '') === token
    );
    return byName ? byName[0] : null;
  };

  if (Array.isArray(subjectReleased) && subjectReleased.length > 0) {
    return subjectReleased
      .map(resolveOne)
      .filter((id): id is string => id !== null);
  }

  return subjectNamesToIds(courseData, scheduleSubjectNames).filter((id) => !!courseData[id]);
};
